/**
 * CHASSIS CUT: Operator Hybrid — 12-Week Program
 *
 * Advanced hybrid training combining strength signals, sled work,
 * carries, Zone 2 conditioning, and armor core.
 *
 * Structure:
 * - Weeks 1-3: Accumulation (volume build, RPE 6-7)
 * - Week 4: Deload + Abbreviated KPI Retest
 * - Weeks 5-7: Intensification (push intensity, RPE 7-8)
 * - Week 8: Deload + Abbreviated KPI Retest
 * - Weeks 9-11: Realization (peak performance, RPE 8-9)
 * - Week 12: Deload + Final KPI Retest
 *
 * KPIs tested every 4th week (single Day 1 assessment):
 *   Strict pull-ups max, Sled push 10x20m timed, Farmer carry 12 min,
 *   Z2 30 min distance, 2K row or 10-min bike
 * Day 2: Z2 recovery. Days 3-6: normal training at deload intensity.
 *
 * 6 days/week — Day 7 = full rest
 */

import type {
  WorkoutProgram,
  WorkoutTemplateDay,
  ProgramMetadata,
  ProgramPeriodizationModel,
} from '../types/program';

// ============================================================================
// METADATA
// ============================================================================

const metadata: ProgramMetadata = {
  id: 'chassis-cut-operator-hybrid',
  name: 'CHASSIS CUT: Operator Hybrid',
  description:
    'Advanced hybrid training combining strength signals, sled work, carries, Zone 2 conditioning, and armor core. 6 days/week with two Redline sessions, two mandatory Z2 days, and abbreviated KPI retests every 4th week.',
  level: 'operator',
  experienceLevel: 'advanced',
  duration: 12,
  daysPerWeek: 6,
  goals: ['strength', 'conditioning', 'work-capacity', 'body-composition', 'tactical-performance'],
  difficulty: 'advanced',
  tags: ['hybrid', 'sled', 'carries', 'zone-2', 'metcon'],
  emphasis: 'Hybrid strength + conditioning with sled and carry integration',
  version: '2.2.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: true,
  hasRangeDay: false,
  // KPI retest cadence — Days 1/3/5 of each deload week have a fixed-
  // load benchmark structure that's distinct enough from both the SF
  // fitness test and a normal workout day to warrant its own tracker.
  // Routes to /assessment/kpi/{day}. See lib/kpiSystem.ts.
  assessmentFormat: 'chassis-kpi',
  requiredEquipment: 'full-gym',
  // Chassis Cut runs a hybrid fat-loss + strength split — Z2 base is
  // load-bearing for the cut. ~150 min/wk Z2 across the dedicated
  // cardio days + strength-day finishers.
  weeklyZ2Target: 150,
};

// ============================================================================
// PERIODIZATION (Block Style)
// ============================================================================

const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    {
      weeks: [1, 2, 3],
      type: 'accumulation',
      name: 'Accumulation Block',
      focus: 'Build volume, establish movement patterns, develop work capacity. Target RPE 6-7 (RIR 3-4)',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.72,
        weeklyIncrease: 0.023,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s',
      },
    },
    {
      weeks: [4],
      type: 'deload',
      name: 'Deload + KPI Retest #1',
      focus: 'Day 1: Full KPI assessment. Day 2: Z2 recovery. Days 3-6: deload training at RPE 5-6 (RIR 4+)',
      intensityMultiplier: 0.7,
      volumeMultiplier: 0.6,
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.6,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [5, 6, 7],
      type: 'intensification',
      name: 'Intensification Block',
      focus: 'Increase intensity, maintain volume, push strength signals. Target RPE 7-8 (RIR 2-3)',
      intensityMultiplier: 1.1,
      volumeMultiplier: 0.9,
      progression: {
        type: 'linear',
        startIntensity: 0.75,
        endIntensity: 0.85,
        weeklyIncrease: 0.033,
      },
      trainingParameters: {
        repRange: [6, 10],
        setsMultiplier: 1.0,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [8],
      type: 'deload',
      name: 'Deload + KPI Retest #2',
      focus: 'Day 1: Full KPI assessment. Day 2: Z2 recovery. Days 3-6: deload training at RPE 5-6 (RIR 4+)',
      intensityMultiplier: 0.7,
      volumeMultiplier: 0.6,
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.6,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [9, 10, 11],
      type: 'realization',
      name: 'Realization Block',
      focus: 'Peak performance — express accumulated strength and conditioning. Target RPE 8-9 (RIR 1-2)',
      intensityMultiplier: 1.15,
      volumeMultiplier: 0.85,
      progression: {
        type: 'linear',
        startIntensity: 0.85,
        endIntensity: 0.92,
        weeklyIncrease: 0.023,
      },
      trainingParameters: {
        repRange: [4, 8],
        setsMultiplier: 1.1,
        restPeriod: '2-3min',
      },
    },
    {
      weeks: [12],
      type: 'deload',
      name: 'Deload + Final KPI Retest',
      focus: 'Final KPI retest — compare against Week 4 and Week 8 results. Day 1: full assessment. Day 2: Z2 recovery. Days 3-6: light deload training',
      intensityMultiplier: 0.7,
      volumeMultiplier: 0.5,
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.5,
        restPeriod: '90-120s',
      },
    },
  ],
};

// ============================================================================
// DELOAD WEEK TEMPLATES (Weeks 4, 8, 12)
// Day 1: Full KPI assessment (all tests in one session)
// Day 2: Z2 recovery
// Days 3-6: Normal training templates with deload multipliers applied
// Day 7: Rest
// ============================================================================

// KPI testing split across three days of the deload week so each test
// is a fresh max effort. The previous design stacked five tests in one
// 75-min day — Pull-Ups + Sled + 12-min Carry + 30-min Z2 + 2K Row.
// Pulling 100% reps cold then sledding then carrying then Z2-walking
// then rowing all-out is recovery debt without a clean benchmark; each
// later test came back depressed by the earlier ones.
//
// Now:
//   Day 1 (Mon): Strength + Sled — short, max-effort
//   Day 3 (Wed): Carry — fresh grip + posterior chain
//   Day 5 (Fri): Z2 + Power — Z2 acts as the warm-up for the row/bike
// Day 2/4 stay as Z2 Recovery (existing deloadZ2Day), Day 6 falls through
// to a training-day template with deload multipliers.

const deloadKpiDayStrength: WorkoutTemplateDay = {
  name: 'KPI — Strength + Sled',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or incline walk' },
        { name: 'TKE', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Strength KPI',
      type: 'testing',
      exercises: [
        { name: 'Strict Pull-Ups', sets: 1, reps: 'Max', intensity: 'Bodyweight', rest: '3 min', details: 'Fresh — do first. Max reps, strict, no kip. Record total.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Sled KPI',
      type: 'testing',
      exercises: [
        { name: 'Sled Push', sets: 10, reps: '20m', intensity: 'Fixed load', rest: 'As needed', details: 'Use SAME load every retest. Record total time for all 10 trips.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
      ],
    },
  ],
};

const deloadKpiDayCarry: WorkoutTemplateDay = {
  name: 'KPI — Work Capacity Carry',
  sessionType: 'assessment',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Light walk or bike to warm the spine' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep' },
      ],
    },
    {
      name: 'Carry KPI',
      type: 'testing',
      exercises: [
        { name: 'Farmer Carry', sets: 1, duration: '12 min', intensity: 'Fixed load', details: 'Use SAME load every retest. Walk continuously 12 min — record total distance. Fresh grip means a real benchmark.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

const deloadKpiDayAerobic: WorkoutTemplateDay = {
  name: 'KPI — Aerobic + Power',
  sessionType: 'assessment',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep' },
      ],
    },
    {
      name: 'Z2 KPI',
      type: 'testing',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '30 min', details: 'Z2 at SAME HR/effort every retest. Incline walk, bike, or row. Record total distance.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Power KPI',
      type: 'testing',
      exercises: [
        { name: '2K Row or 10-Min Bike', sets: 1, reps: '1 effort', intensity: 'Max', rest: '5 min after Z2', details: 'Z2 was the warm-up — now best effort. Record time (2K row) or distance (10-min bike).', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

const deloadZ2Day: WorkoutTemplateDay = {
  name: 'Deload Z2 Recovery',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep' },
        { name: 'TKE', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30-45s/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Zone 2 Conditioning',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '35-50 min', details: 'Incline walk, bike, or row. Maintain Z2 HR — conversational pace. Easy recovery after KPI day.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// TRAINING CYCLE TEMPLATES (All non-deload weeks)
// ============================================================================

// --- DAY 1: LOWER A (Quads + Power + Core + Sled Push) ---
const trainingDay1: WorkoutTemplateDay = {
  name: 'Lower A — Quads + Power + Sled',
  sessionType: 'pt',
  estimatedDuration: 85,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Bike or incline walk' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30-45s', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 1, reps: '10-15 steps each way', format: 'prep' },
        { name: 'Step-Downs', sets: 2, reps: '6-10/side', format: 'prep', details: '3-sec eccentric' },
        { name: 'Back Squat Ramp', sets: 2, reps: '8-10', format: 'prep', details: 'Progressive warm-up sets — build to working squat weight' },
      ],
    },
    {
      name: 'Power',
      type: 'strength',
      exercises: [
        { name: 'Med Ball Scoop Toss', sets: 3, reps: '3-5/side', intensity: 'explosive', details: 'Or slam variation' },
      ],
    },
    {
      name: 'Strength Signal',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: 5, intensity: 'RIR 2', rest: '2-3 min', details: 'Heavy squat — brace hard, full depth, drive through midfoot. Alt: Front Squat, or Goblet Squat 4x8 if mobility-limited' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Leg Press', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00', details: '3-sec eccentric — accessory quad volume after the squat' },
        { name: 'Leg Press Calf Raise', sets: 4, reps: '10-15', intensity: 'progressive', rest: '90s', details: 'Calf press on the leg press machine — no need to move' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Leg Extension', sets: 3, reps: '12-20', intensity: 'progressive', rest: '0:00', details: '1-sec squeeze at top' },
        { name: 'Seated Ham Curl', sets: 3, reps: '10-15', intensity: 'progressive', rest: '60-90s' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: '8-12', intensity: 'progressive', rest: '0:00', details: 'Use dumbbells, moderate weight' },
        { name: 'Hip Abduction', sets: 3, reps: '12-20/side', intensity: 'progressive', rest: '60-90s', details: 'Cable hip abduction' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '10-15/side', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Sled + Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 8, reps: '15-25m', intensity: 'heavy', rest: '45-60s', details: 'Accumulation: 8-10 trips. Intensification/Realization: cap at 6 trips' },
        { name: 'Farmer Carry', sets: 4, reps: '30-50m', intensity: 'progressive', rest: '60s', details: '3-4 sets' },
      ],
    },
    {
      name: 'Optional Z2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '10-20 min', details: 'Optional — incline walk, bike, or row at Z2. Only if recovered.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Calf Stretch', duration: '45s each/side', format: 'recovery', details: 'Straight + bent knee' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 2: UPPER A (Strength Signal + Push/Pull + Suitcase + HIIT) REDLINE #1 ---
const trainingDay2: WorkoutTemplateDay = {
  name: 'Upper A — Strength Signal + HIIT [REDLINE]',
  sessionType: 'pt',
  estimatedDuration: 80,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower' },
        { name: 'Scapular Push-Ups', sets: 1, reps: '12-15', format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12-15/side', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: '15-20', format: 'prep' },
        { name: 'T-Spine Extension', sets: 1, reps: '6-8', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 2, reps: 10, format: 'prep' },
        { name: 'Pull-Up Ramp', sets: 2, reps: '3-5', format: 'prep', details: 'Bodyweight or light band assist, then add weight progressively' },
      ],
    },
    {
      name: 'Strength Signal',
      type: 'strength',
      exercises: [
        { name: 'Weighted Pull-Up', sets: 5, reps: 3, intensity: 'RIR 2', rest: '2-3 min', details: 'Strict. Alt: slow tempo pull-ups 5x3 with 3-sec lower' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 4, reps: '6-10', intensity: 'progressive', rest: '0:00' },
        { name: 'Chest Supported Row', sets: 4, reps: '6-10', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: '6-10', intensity: 'progressive', rest: '0:00', details: 'Neutral grip' },
        { name: 'Machine Chest Press', sets: 2, reps: '8-12', intensity: 'progressive', rest: '60-90s', details: 'Reduced volume to protect shoulder' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Cable Lateral Raise', sets: 3, reps: '12-20', intensity: 'progressive', rest: '0:00' },
        { name: 'Reverse Pec Deck', sets: 3, reps: '12-20', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Arms',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hammer Curl', sets: 3, reps: '10-15', intensity: 'progressive', rest: '0:00' },
        { name: 'Overhead Tricep Extension', sets: 3, reps: '10-15', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Suitcase Carry', sets: 4, reps: '30-60m/side', intensity: 'progressive', rest: '45-60s' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Hanging Knee Raise', sets: 3, reps: '8-15', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'HIIT',
      type: 'conditioning',
      exercises: [
        { name: 'Row/Bike Sprint', sets: 8, reps: 1, format: 'intervals', details: '20s hard / 55s easy — bike or row', rest: '55s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s', format: 'recovery' },
        { name: 'Band External Rotation', sets: 1, reps: 20, format: 'recovery', details: 'Flush set — easy' },
        { name: '90/90 Breathing', duration: '1-2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 3: BASE + CARRIES + CHASSIS (No Redline, Mandatory Z2) ---
const trainingDay3: WorkoutTemplateDay = {
  name: 'Base + Carries + Chassis [Mandatory Z2]',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep' },
        { name: 'TKE', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30-45s/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Zone 2 Conditioning',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '35-50 min', details: 'Incline walk, bike, row, or light ruck (monthly). Maintain Z2 HR — conversational pace.' },
      ],
    },
    {
      name: 'Carry Density',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 6, reps: '40-60m', intensity: 'progressive', rest: '45s', details: '6 rounds — moderate to heavy' },
      ],
    },
    {
      name: 'Chassis Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'Pallof Press', sets: 2, reps: '12/side', rest: '0:00' },
        { name: 'Side Plank', sets: 2, reps: '30-45s/side', rest: '0:00' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 4: LOWER B (Hinge Signal + Posterior + Backward Drag + Short Z2) ---
const trainingDay4: WorkoutTemplateDay = {
  name: 'Lower B — Hinge Signal + Posterior + Drag',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Bike' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep' },
        { name: 'Seated Ham Curl', sets: 1, reps: 15, format: 'prep', details: 'Light — warm-up only' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '20-30s + 8 reps', format: 'prep' },
        { name: 'Frog Stretch', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Soleus Raise', sets: 2, reps: '12-20', format: 'prep' },
        { name: 'Split-Stance Hinge', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Hip Thrust Ramp', sets: 2, reps: '8-10', format: 'prep', details: 'Progressive warm-up sets — build to working weight' },
      ],
    },
    {
      name: 'Strength Signal',
      type: 'strength',
      exercises: [
        { name: 'Barbell RDL', sets: 4, reps: 6, intensity: 'RIR 2', rest: '2-3 min', details: 'Heavy hinge — strict hip hinge, minimal knee bend. Alt: Dumbbell RDL 4x8 if barbell unavailable' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Seated Ham Curl', sets: 4, reps: '8-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Tibialis Raise', sets: 4, reps: '12-20', intensity: 'progressive', rest: '60-90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Hip Thrust', sets: 4, reps: '8-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Cable Crunch', sets: 4, reps: '10-15', intensity: 'progressive', rest: '60-90s' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 3, reps: '8-12/side', intensity: 'progressive', rest: '0:00', details: 'Controlled tempo, full depth. Alt: high-foot leg press if no barbell/DB setup' },
        { name: 'Back Extension', sets: 3, reps: '10-15', intensity: 'progressive', rest: '60-90s' },
      ],
    },
    {
      name: 'Backward Sled Drag',
      type: 'conditioning',
      exercises: [
        { name: 'Backward Drag', sets: 10, reps: '15-25m', intensity: 'progressive', rest: '30-45s', details: '8-14 trips — backward sled drag, moderate weight' },
      ],
    },
    {
      name: 'Short Z2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '10-20 min', details: 'Easy incline walk or bike — Z2 pace' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 5: UPPER B (Strength Signal Option + Pull Emphasis + Z2 + Bottoms-Up Carry) Mandatory Z2 ---
const trainingDay5: WorkoutTemplateDay = {
  name: 'Upper B — Pull Emphasis + Z2 [Mandatory Z2]',
  sessionType: 'pt',
  estimatedDuration: 80,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower' },
        { name: 'Dead Hang', duration: '20-40s', format: 'prep' },
        { name: 'Straight-Arm Pulldown', sets: 2, reps: '12-15', format: 'prep' },
        { name: '1-Arm Cable Row', sets: 1, reps: '12/side', format: 'prep', details: 'Pause reps — warm-up' },
        { name: 'Shoulder Y-T-W', sets: 1, reps: '6 each', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 1, reps: '10-12', format: 'prep' },
        { name: 'Pulldown/Press Ramp', sets: 2, reps: '6-8', format: 'prep', details: 'Progressive warm-up — pulldown or press depending on strength signal choice' },
      ],
    },
    {
      name: 'Optional Strength Signal',
      type: 'strength',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 6, reps: 3, intensity: 'RIR 2', rest: '2-3 min', details: 'OPTIONAL: Skip if Day 2 felt heavy. Alt: Weighted Chin-Up 5x3 @ RIR 2' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Lat Pulldown', sets: 4, reps: '8-12', intensity: 'progressive', rest: '0:00', details: 'Neutral grip' },
        { name: 'Landmine Press', sets: 4, reps: '8-12/side', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: '1-Arm Cable Row', sets: 3, reps: '10-12/side', intensity: 'progressive', rest: '0:00' },
        { name: 'Dumbbell Bench Press with Neutral Grip', sets: 3, reps: '8-12', intensity: 'progressive', rest: '60-90s', details: 'Flat press — a different angle from Day 2 and the incline signal, so the week is not all incline' },
      ],
    },
    {
      name: 'Superset C',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Reverse Pec Deck', sets: 3, reps: '12-20', intensity: 'progressive', rest: '0:00' },
        { name: 'Dumbbell Shrug', sets: 3, reps: '10-15', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Arms',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Barbell Curl', sets: 3, reps: '8-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Triceps Pressdown', sets: 3, reps: '10-15', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Bottoms-Up KB Carry', sets: 4, reps: '15-25m/side', intensity: 'progressive', rest: '30-45s', details: '3-5 sets per side' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Side Plank', sets: 3, reps: '20-45s/side', rest: '30s' },
      ],
    },
    {
      name: 'Zone 2 Conditioning',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '15-30 min', details: 'Incline walk or bike — Z2 pace (mandatory)' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery', details: 'Lat/teres stretch' },
        { name: 'Wall Pec Stretch', duration: '45-60s/side', format: 'recovery' },
        { name: 'Face Pull', sets: 1, reps: 20, format: 'recovery', details: 'Flush set — easy' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 6: METCON + ARMOR CORE (REDLINE #2) ---
const trainingDay6: WorkoutTemplateDay = {
  name: 'Hard Metcon + Armor Core [REDLINE]',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2-3 min', format: 'prep' },
        { name: "World's Greatest Stretch", sets: 1, reps: '4/side', format: 'prep' },
        { name: 'Hip Airplanes', sets: 1, reps: '5/side', format: 'prep', details: 'Supported' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
        { name: 'Pallof Press', sets: 1, reps: '10/side', format: 'prep', details: 'Light — activation' },
        { name: 'Box Squat', sets: 1, reps: 8, format: 'prep', details: 'Light — movement prep' },
      ],
    },
    {
      name: 'AMRAP Metcon',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Sled Push', reps: '20m', format: 'circuit' },
        { name: 'Farmer Carry', reps: '40m', format: 'circuit' },
        { name: 'Pull-Ups', reps: '8-12', format: 'circuit', details: 'Or Lat Pulldown 12-15' },
        { name: 'Landmine Press', reps: '10/side', format: 'circuit' },
      ],
    },
    {
      name: 'Armor Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'McGill Curl-Up', sets: 2, reps: 8, rest: '0:00', details: '10-sec holds' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '0:00' },
        { name: 'Pallof Press', sets: 2, reps: '12/side', rest: '60s' },
      ],
    },
    {
      name: 'Optional Workload',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '20-40 min', details: 'OPTIONAL: Ruck-lite or incline walk. Only if recovered.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- DAY 7: REST ---
const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const chassisCutOperatorHybrid: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [], // Legacy field
  templates: {
    // Training cycle (all weeks — deload multipliers applied automatically by phase)
    'training-day1': trainingDay1,
    'training-day2': trainingDay2,
    'training-day3': trainingDay3,
    'training-day4': trainingDay4,
    'training-day5': trainingDay5,
    'training-day6': trainingDay6,
    'training-day7': restDay,

    // Deload + KPI weeks — KPI tests split Mon/Wed/Fri so each is a
    // fresh max effort. Z2 recovery on Tue/Thu, light training falls
    // through on Sat with deload multipliers, Sun is rest.
    'week4-day1': deloadKpiDayStrength,
    'week4-day2': deloadZ2Day,
    'week4-day3': deloadKpiDayCarry,
    'week4-day4': deloadZ2Day,
    'week4-day5': deloadKpiDayAerobic,
    'week8-day1': deloadKpiDayStrength,
    'week8-day2': deloadZ2Day,
    'week8-day3': deloadKpiDayCarry,
    'week8-day4': deloadZ2Day,
    'week8-day5': deloadKpiDayAerobic,
    'week12-day1': deloadKpiDayStrength,
    'week12-day2': deloadZ2Day,
    'week12-day3': deloadKpiDayCarry,
    'week12-day4': deloadZ2Day,
    'week12-day5': deloadKpiDayAerobic,
  },
};
